import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import type {
    IImportedSpirometerData,
    IKlachten,
    IMrc,
} from '../../../../../../common/validation/customControls/pulmonology';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IAsset } from '../../../../interfaces/asset';
import type { IAcq } from './AnamneseAcq';
import type { ICcq } from './AnamneseCcq';
import type { ISpirogramData } from './SpirogramData';
import { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import { DesktopProcessActions } from '../../../../../../common/enums/desktopProcessActions';
import { DesktopProcessStatus } from '../../../../../../common/enums/desktopProcessStatus';
import { Gender } from '../../../../../../common/enums/gender';
import { GenderSpirometer } from '../../../../../../common/enums/genderSpirometer';
import { HardwareProcessStatus } from '../../../../../../common/enums/hardwareProcessStatus';
import { SpirometerSoftwareNames } from '../../../../../../common/enums/spirometerSoftwareNames';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { BmiText } from '../../../../elements/BmiText';
import { CheckMarkButton } from '../../../../elements/buttons/checkMarkButton/CheckMarkButton';
import {
    Button,
    FormAnamnesisButtons,
    FormDivider,
    FormOverlayMessage,
    FormParagraph,
    HeaderDivider,
    InputNumber,
    SecondHeader,
    ThirdHeader,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { openKsyosUrl, OpenKsyosUrlResult } from '../../../../elements/openKsyosUrl';
import { DateField } from '../../../../fields/Date';
import { SwitchField } from '../../../../fields/Switch';
import { ProcedureAssetThumbnails, RequestAssetThumbnails } from '../../../episodes/common';
import { AnamneseAcq } from './AnamneseAcq';
import { AnamneseCcq } from './AnamneseCcq';
import { AnamneseKlachten } from './AnamneseKlachten';
import { AnamneseMrc } from './AnamneseMrc';
import { AnamneseType } from './anamneseType';
import { desktopProcessErrorCodeText } from './desktopProcessErrorCodeText';
import { SpirogramData } from './SpirogramData';

interface ISpirometryAddEditProps {
    afterShowSpirometerModal: () => Promise<void>;
    customControlData: ICustomControlData;
    employmentId: string;
    errors: ValidationErrors;
    handleChange: (customControl: Partial<ICustomControlData>) => Promise<void>;
    organizationId: string;
    patientId: string;
    procedureId?: string;
    pulmoType: 'procedure' | 'request';
    requestId?: string;
    saveAsDraft: () => Promise<string | undefined>;
    serviceConsumer?: {
        spirometerSoftwareName: SpirometerSoftwareNames | null;
        usingSpirometer: boolean;
    };
    serviceId: string;
    serviceProvider?: {
        spirometerSoftwareName: SpirometerSoftwareNames | null;
        usingSpirometer: boolean;
    };
    setIsProcessing: (isProcessing: boolean) => Promise<void>;
    showSpirometerModal?: boolean;
    validateOne: (name: string) => Promise<void>;
}

export interface ICustomControlData {
    ACQ: IAcq | null;
    CCQ: ICcq | null;
    conclusie_diagnose?: ConclusieDiagnoseOption;
    datum_afname_functieonderzoek?: string | null;
    desktopProcessId?: string;
    files: IAsset[];
    gender: Gender;
    gewicht: number | null;
    importedSpirometerData: IImportedSpirometerData | null;
    keten_DBC_COPD_astma?: boolean;
    klachten: IKlachten | null;
    lengte: number | null;
    MRC: IMrc | null;
    spirogram_inc_reversibiliteitstest?: boolean;
    spirogramData: ISpirogramData | null;
}

interface ISpirometryAddEditState {
    fileUploadErrorMessage?: string;
    hasDesktopProcessTimedOut: boolean;
    isMissingKsyosUrlHandler: boolean;
    procedureId?: string;
    processError?: string;
    processStatus?: HardwareProcessStatus;

    requestId?: string;
    show: AnamneseType | 'spirogram' | null;
}

const timeoutIntervalSeconds = 2;
const timeoutIntervalMilliseconds = timeoutIntervalSeconds * 1000;
const maxWaitTimeSeconds = 30;

export class SpirometryAddEdit extends Component<ISpirometryAddEditProps, ISpirometryAddEditState> {
    private desktopProcessUpdateInterval: number;

    constructor(props: ISpirometryAddEditProps) {
        super(props);
        this.state = {
            show: null,
            isMissingKsyosUrlHandler: false,
            hasDesktopProcessTimedOut: false,
        };
    }

    public async componentDidMount() {
        await this.getPatientGender();
        await this.getDesktopProcessStatus();
    }

    public async componentWillUnmount() {
        if (this.desktopProcessUpdateInterval) {
            clearInterval(this.desktopProcessUpdateInterval);
        }
    }

    public async componentDidUpdate() {
        if (this.props.showSpirometerModal) {
            await this.props.afterShowSpirometerModal();
            await this.setState({ show: 'spirogram' });
        }
    }

    public render() {
        return (
            <FormContextProvider
                value={{
                    placeholderGroupName: 'request',
                    item: this.props.customControlData,
                    errors: this.props.errors,
                    onChange: async <K extends keyof ICustomControlData>(name: K, value: ICustomControlData[K]) =>
                        await this.props.handleChange({ [name]: value }),
                    onDoValidate: async (key: keyof ICustomControlData) => await this.props.validateOne(key),
                }}
            >
                <>
                    <FormGroup>
                        <FormParagraph>
                            {this.state.isMissingKsyosUrlHandler && (
                                <span>
                                    Download nieuwe Ksyos spirometerkoppeling{' '}
                                    <a
                                        href={`/desktop?organizationId=${this.props.organizationId}`}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        hier
                                    </a>
                                    .
                                </span>
                            )}
                            {this.spirometerStatus()}
                            {!this.spirometerStatus() &&
                                ((this.props.serviceProvider && this.props.serviceProvider.usingSpirometer) ||
                                    (this.props.serviceConsumer && this.props.serviceConsumer.usingSpirometer)) && (
                                    <>
                                        Heb je de nieuwe spirometerkoppeling al geïnstalleerd? Klik{' '}
                                        <a href={`/desktop?organizationId=${this.props.organizationId}`}>hier</a> om te
                                        downloaden.
                                    </>
                                )}
                        </FormParagraph>
                    </FormGroup>

                    {this.props.customControlData.desktopProcessId && this.isDesktopProcessRunning() && (
                        <FormOverlayMessage>
                            {this.state.processStatus === HardwareProcessStatus.starting && (
                                <SecondHeader text="Het spirometer onderzoek is gestart" />
                            )}
                            {this.state.processStatus === HardwareProcessStatus.running && (
                                <SecondHeader text="Het spirometer onderzoek is lopende" />
                            )}
                            {this.state.processStatus === HardwareProcessStatus.uploading && (
                                <SecondHeader text="Het spirometer onderzoek wordt geüpload" />
                            )}

                            <Button
                                color="primary"
                                onClick={async () => await this.cancelDesktopProcess()}
                            >
                                Annuleer import
                            </Button>

                            {this.state.hasDesktopProcessTimedOut && (
                                <ThirdHeader text="Er lijkt iets mis te gaan met de Ksyos koppeling. Neem contact op met Ksyos om dit probleem op te lossen." />
                            )}
                        </FormOverlayMessage>
                    )}
                </>

                {this.props.pulmoType === 'request' &&
                    (this.state.requestId || this.props.requestId) &&
                    this.props.customControlData &&
                    this.props.customControlData.files.length > 0 && (
                        <FormGroup>
                            <FormLabel>{translateKey('request.attachment')}</FormLabel>
                            <RequestAssetThumbnails
                                assets={this.props.customControlData.files as IAsset[]}
                                requestId={this.state.requestId ? this.state.requestId : this.props.requestId!}
                            />
                        </FormGroup>
                    )}
                {this.props.pulmoType === 'procedure' &&
                    (this.state.procedureId || this.props.procedureId) &&
                    this.props.customControlData &&
                    this.props.customControlData.files.length > 0 && (
                        <FormGroup>
                            <FormLabel>{translateKey('request.attachment')}</FormLabel>
                            <ProcedureAssetThumbnails
                                assets={this.props.customControlData.files as IAsset[]}
                                procedureId={this.state.procedureId ? this.state.procedureId : this.props.procedureId!}
                            />
                        </FormGroup>
                    )}
                {this.props.pulmoType === 'procedure' && (
                    <>
                        {this.props.customControlData.keten_DBC_COPD_astma !== undefined && (
                            <SwitchField
                                fieldKey="keten_DBC_COPD_astma"
                                label="KetenDBC COPD/astma?"
                            />
                        )}
                        {this.props.customControlData.spirogram_inc_reversibiliteitstest !== undefined && (
                            <SwitchField
                                fieldKey="spirogram_inc_reversibiliteitstest"
                                label="Spirogram incl. reversibiliteitstest?"
                            />
                        )}
                        {this.props.customControlData.datum_afname_functieonderzoek !== undefined && (
                            <DateField
                                error={this.props.errors.get('customControlData.datum_afname_functieonderzoek')}
                                fieldKey="datum_afname_functieonderzoek"
                                hasErrors={this.props.errors.has('customControlData.datum_afname_functieonderzoek')}
                                isRequired
                                label="Datum functieonderzoek"
                                placeholder={translateKey('patient.birthDatePlaceholder')}
                                size="medium"
                            />
                        )}
                    </>
                )}

                <HorizontalFormGroup
                    hasErrors={
                        this.props.errors.has('customControlData.gewicht') ||
                        this.props.errors.has('customControlData.lengte')
                    }
                >
                    <FormLabel>BMI</FormLabel>
                    <InputContainer size="auto">
                        <InputNumber
                            automation="bmiWeight"
                            disabled={this.state.processStatus === HardwareProcessStatus.success}
                            fieldKey="gewicht"
                            placeholder={translateKey('common.weight')}
                            text="kg"
                            textStyle={{ fontWeight: 'bold' }}
                            width="medium"
                        />
                    </InputContainer>
                    <InputContainer size="auto">
                        <InputNumber
                            automation="bmiHeight"
                            disabled={this.state.processStatus === HardwareProcessStatus.success}
                            fieldKey="lengte"
                            placeholder={translateKey('common.length')}
                            text="cm"
                            textStyle={{ fontWeight: 'bold' }}
                            width="medium"
                        />
                    </InputContainer>
                    <InputContainer size="small">
                        <BmiText
                            length={this.props.customControlData.lengte}
                            weight={this.props.customControlData.gewicht}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <FormDivider />
                <HeaderDivider text={translateKey('carePlan.anamnese')} />
                <HorizontalFormGroup>
                    <FormLabel>Anamnese toevoegen</FormLabel>
                    <InputContainer>
                        <FormAnamnesisButtons
                            buttons={[
                                <CheckMarkButton
                                    automation="complaints"
                                    isChecked={!!this.props.customControlData.klachten}
                                    onClick={async () => await this.setState({ show: AnamneseType.klachten })}
                                >
                                    Klachten
                                </CheckMarkButton>,
                                <CheckMarkButton
                                    automation="mrcQuestions"
                                    isChecked={!!this.props.customControlData.MRC}
                                    onClick={async () => await this.setState({ show: AnamneseType.MRC })}
                                >
                                    MRC vragen
                                </CheckMarkButton>,
                                <CheckMarkButton
                                    automation="ccqQuestions"
                                    isChecked={!!this.props.customControlData.CCQ}
                                    onClick={async () => await this.setState({ show: AnamneseType.CCQ })}
                                >
                                    CCQ vragen
                                </CheckMarkButton>,
                                <CheckMarkButton
                                    automation="acqQuestions"
                                    isChecked={!!this.props.customControlData.ACQ}
                                    onClick={async () => await this.setState({ show: AnamneseType.ACQ })}
                                >
                                    ACQ vragen
                                </CheckMarkButton>,
                            ]}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <FormDivider />

                {this.state.show === 'spirogram' && (
                    <SpirogramData
                        onClose={async () => await this.setState({ show: null })}
                        setSpirogramData={async (spirometerData) => this.startSpirometer(spirometerData)}
                        spirogramData={this.getSpirogramData()}
                        spirometerSoftwareName={this.getSpirometerSoftwareName()}
                    />
                )}

                {this.state.show === AnamneseType.klachten && (
                    <AnamneseKlachten
                        anamneseData={this.props.customControlData.klachten}
                        deleteAnamneseData={async () => await this.deleteAnamneseData(AnamneseType.klachten)}
                        initialRookgedrag={this.getRookgedragKlachten()}
                        onClose={async () => await this.setState({ show: null })}
                        setAnamneseData={async (item) => {
                            await this.setAnamneseData(item, AnamneseType.klachten);
                            await this.setRookgedragKlachten(item);
                        }}
                    />
                )}

                {this.state.show === AnamneseType.MRC && (
                    <AnamneseMrc
                        anamneseData={this.props.customControlData.MRC}
                        deleteAnamneseData={async () => await this.deleteAnamneseData(AnamneseType.MRC)}
                        onClose={async () => await this.setState({ show: null })}
                        setAnamneseData={async (item) => await this.setAnamneseData(item, AnamneseType.MRC)}
                    />
                )}

                {this.state.show === AnamneseType.CCQ && (
                    <AnamneseCcq
                        anamneseData={this.props.customControlData.CCQ}
                        deleteAnamneseData={async () => await this.deleteAnamneseData(AnamneseType.CCQ)}
                        onClose={async () => await this.setState({ show: null })}
                        setAnamneseData={async (item) => await this.setAnamneseData(item, AnamneseType.CCQ)}
                    />
                )}

                {this.state.show === AnamneseType.ACQ && (
                    <AnamneseAcq
                        anamneseData={this.props.customControlData.ACQ}
                        deleteAnamneseData={async () => await this.deleteAnamneseData(AnamneseType.ACQ)}
                        onClose={async () => await this.setState({ show: null })}
                        setAnamneseData={async (item) => await this.setAnamneseData(item, AnamneseType.ACQ)}
                    />
                )}
            </FormContextProvider>
        );
    }

    private async setAnamneseData(item: any, typeOfAnamnese: AnamneseType) {
        await this.setState({ show: null });
        await this.props.handleChange({ [typeOfAnamnese]: { ...item } });
    }

    private getRookgedragKlachten() {
        if (this.props.customControlData.klachten) {
            return this.props.customControlData.klachten.rookgedrag;
        } else if (
            this.props.customControlData.spirogramData &&
            this.props.customControlData.spirogramData.rookgedrag
        ) {
            return this.props.customControlData.spirogramData.rookgedrag;
        }

        return null;
    }

    private async setRookgedragKlachten(item: IKlachten) {
        if (this.props.customControlData.spirogramData) {
            await this.setState({ show: null });
            await this.props.handleChange({
                spirogramData: {
                    ...this.props.customControlData.spirogramData,
                    rookgedrag: item.rookgedrag,
                },
            });
        }
    }

    private async deleteAnamneseData(typeOfAnamnese: AnamneseType) {
        await this.setState({ show: null });
        await this.props.handleChange({ [typeOfAnamnese]: null });
    }

    private spirometerStatus() {
        if (this.state.processStatus === HardwareProcessStatus.starting) {
            return 'Status: Onderzoek gestart';
        } else if (this.state.processStatus === HardwareProcessStatus.running) {
            return 'Status: Onderzoek lopend';
        } else if (this.state.processStatus === HardwareProcessStatus.uploading) {
            return 'Status: Onderzoek uploaden';
        } else if (this.state.processStatus === HardwareProcessStatus.success) {
            if (
                this.props.customControlData.importedSpirometerData &&
                this.props.customControlData.importedSpirometerData.afgerondOp
            ) {
                return `Status: Onderzoek afgerond op ${DateTime.from(
                    this.props.customControlData.importedSpirometerData.afgerondOp
                ).format('DD-MM-YYYY')}`;
            }

            return 'Status: Onderzoek afgerond';
        } else if (this.state.processStatus === HardwareProcessStatus.failed) {
            if (this.state.processError) {
                const errorCode = this.state.processError;
                if (desktopProcessErrorCodeText[errorCode]) {
                    return (
                        <Alert appearance="warning">
                            Fout tijdens het afhandelen van het onderzoek: {desktopProcessErrorCodeText[errorCode]}
                        </Alert>
                    );
                }
            }

            return <Alert appearance="warning">Status: Onderzoek gefaald</Alert>;
        } else if (this.state.processError) {
            return <Alert appearance="warning">{this.state.processError}</Alert>;
        }
    }

    private getSpirogramData() {
        const data: ISpirogramData = {
            lengte: this.props.customControlData.lengte,
            gewicht: this.props.customControlData.gewicht,
            gender: null,
            rookgedrag: undefined,
            careFusionEthnicGroup: undefined,
            cardioPerfectEthnicGroup: undefined,
        };
        if (this.props.customControlData.gender === Gender.male) {
            data.gender = GenderSpirometer.male;
        } else if (this.props.customControlData.gender === Gender.female) {
            data.gender = GenderSpirometer.female;
        }
        if (this.props.customControlData.spirogramData) {
            data.rookgedrag = this.props.customControlData.spirogramData.rookgedrag;
        } else if (this.props.customControlData.klachten && this.props.customControlData.klachten.rookgedrag) {
            data.rookgedrag = this.props.customControlData.klachten.rookgedrag;
        }
        if (
            this.props.customControlData.spirogramData &&
            this.props.customControlData.spirogramData.careFusionEthnicGroup
        ) {
            data.careFusionEthnicGroup = this.props.customControlData.spirogramData.careFusionEthnicGroup;
        } else if (
            this.props.customControlData.spirogramData &&
            this.props.customControlData.spirogramData.cardioPerfectEthnicGroup
        ) {
            data.cardioPerfectEthnicGroup = this.props.customControlData.spirogramData.cardioPerfectEthnicGroup;
        }

        return data;
    }

    private async getPatientGender() {
        const patient = await fetchJson(`/api/patients/${this.props.patientId}`, 'GET');
        await this.props.handleChange({ gender: patient.json.gender });
    }

    private getSpirometerSoftwareName() {
        if (this.props.serviceConsumer && this.props.serviceConsumer.spirometerSoftwareName) {
            return this.props.serviceConsumer.spirometerSoftwareName;
        }
        if (this.props.serviceProvider && this.props.serviceProvider.spirometerSoftwareName) {
            return this.props.serviceProvider.spirometerSoftwareName;
        }

        throw new Error('Unknown SpirometerSoftwareName');
    }

    private getDesktopProcessAction(spirometerSoftwareName: string) {
        if (spirometerSoftwareName === SpirometerSoftwareNames.CardioPerfect) {
            if (this.props.pulmoType === 'request') {
                return DesktopProcessActions.spirometry_request_cardioPerfect;
            } else if (this.props.pulmoType === 'procedure') {
                return DesktopProcessActions.spirometry_procedure_cardioPerfect;
            }
        } else if (spirometerSoftwareName === SpirometerSoftwareNames.CareFusion) {
            if (this.props.pulmoType === 'request') {
                return DesktopProcessActions.spirometry_request_careFusion;
            } else if (this.props.pulmoType === 'procedure') {
                return DesktopProcessActions.spirometry_procedure_careFusion;
            }
        }
    }

    private isDesktopProcessRunning() {
        return (
            this.state.processStatus === HardwareProcessStatus.running ||
            this.state.processStatus === HardwareProcessStatus.starting ||
            this.state.processStatus === HardwareProcessStatus.uploading
        );
    }

    private async startSpirometer(spirometerData: ISpirogramData) {
        if (this.isDesktopProcessRunning() && this.props.customControlData.desktopProcessId) {
            return;
        }

        await this.props.setIsProcessing(true);
        await this.props.handleChange({
            lengte: spirometerData.lengte,
            gewicht: spirometerData.gewicht,
            importedSpirometerData: null,
            desktopProcessId: undefined,
            spirogramData: {
                ...this.props.customControlData.spirogramData,
                ...spirometerData,
            },
        });

        await this.setState({
            show: null,
            processStatus: HardwareProcessStatus.starting,
            processError: undefined,
            hasDesktopProcessTimedOut: false,
        });

        if (this.props.customControlData.klachten) {
            await this.props.handleChange({
                klachten: {
                    ...this.props.customControlData.klachten,
                    rookgedrag: spirometerData.rookgedrag || null,
                },
            });
        }

        if (this.desktopProcessUpdateInterval) {
            clearInterval(this.desktopProcessUpdateInterval);
        }

        const spirometerSoftwareName = this.getSpirometerSoftwareName();
        if (!spirometerSoftwareName) {
            throw new Error('Unknown Spirometer software');
        }

        const desktopProcessAction = this.getDesktopProcessAction(spirometerSoftwareName);
        if (!desktopProcessAction) {
            throw new Error('Unknown desktop action');
        }

        const requestId = await this.props.saveAsDraft();
        if (!requestId) {
            await this.setState({ processStatus: undefined });

            return this.props.setIsProcessing(false);
        }

        let procedureId = this.props.procedureId || this.state.procedureId;
        if (
            !procedureId &&
            (desktopProcessAction === DesktopProcessActions.spirometry_procedure_cardioPerfect ||
                desktopProcessAction === DesktopProcessActions.spirometry_procedure_careFusion)
        ) {
            const procedure = await fetchJson(`/api/requests/${requestId}/procedure`, 'GET');
            procedureId = procedure.json.id;
        }
        await this.setState({ requestId, procedureId });

        let ethnicGroup = undefined;
        if (spirometerData.careFusionEthnicGroup) {
            ethnicGroup = spirometerData.careFusionEthnicGroup;
        } else if (spirometerData.cardioPerfectEthnicGroup) {
            ethnicGroup = spirometerData.cardioPerfectEthnicGroup;
        }

        const desktopProcess = await fetchJson('/api/desktopProcesses', 'POST', {
            serviceId: this.props.serviceId,
            action: desktopProcessAction,
            requestId: requestId,
            params: {
                ethnicGroup: ethnicGroup,
                rookgedrag: spirometerData.rookgedrag,
                rokerDuur: spirometerData.rokerDuur,
            },
        });

        const params = {
            employmentId: this.props.employmentId,
            desktopProcessId: desktopProcess.json.id,
            token: desktopProcess.json.token,
            apiUrl: location.protocol + '//' + location.host,
            weight: this.props.customControlData.gewicht,
            height: this.props.customControlData.lengte,
            ethnicGroup: ethnicGroup,
            gender: spirometerData.gender,
            rookgedrag: spirometerData.rookgedrag,
            rokerDuur: spirometerData.rokerDuur,
        };
        await this.props.handleChange({ desktopProcessId: desktopProcess.json.id });

        const url =
            'ksyos://spirometry/' + spirometerSoftwareName.toLowerCase() + '?encoded=' + btoa(JSON.stringify(params));
        const openResult = await openKsyosUrl(url);

        if (openResult === OpenKsyosUrlResult.noHandler) {
            await this.setState({ isMissingKsyosUrlHandler: true, processStatus: undefined });
            await this.props.setIsProcessing(false);
        } else {
            await this.desktopStatusInterval(desktopProcessAction, requestId, procedureId);
        }
    }

    private async desktopStatusInterval(
        desktopProcessAction: DesktopProcessActions,
        requestId: string,
        procedureId?: string
    ) {
        let timeout = 0;
        this.desktopProcessUpdateInterval = window.setInterval(async () => {
            const url = `/api/desktopProcesses/${this.props.customControlData.desktopProcessId}`;
            const result = await fetchJson(url, 'GET');

            const desktopProcess: { data?: { error?: string }; status: DesktopProcessStatus } = result.json;

            if (desktopProcess.status === DesktopProcessStatus.starting) {
                if (timeout >= maxWaitTimeSeconds) {
                    await this.setState({
                        hasDesktopProcessTimedOut: true,
                        processStatus: HardwareProcessStatus.failed,
                    });
                    clearInterval(this.desktopProcessUpdateInterval);
                    await this.props.setIsProcessing(false);
                } else {
                    await this.setState({ processStatus: HardwareProcessStatus.starting });
                    timeout += timeoutIntervalSeconds;
                }
            } else if (desktopProcess.status === DesktopProcessStatus.running) {
                await this.setState({ processStatus: HardwareProcessStatus.running });
            } else if (desktopProcess.status === DesktopProcessStatus.uploading) {
                await this.setState({ processStatus: HardwareProcessStatus.uploading });
            } else if (desktopProcess.status === DesktopProcessStatus.completed) {
                if (desktopProcess.data && desktopProcess.data.error) {
                    await this.setState({
                        processStatus: HardwareProcessStatus.failed,
                        processError: desktopProcess.data.error,
                    });
                } else {
                    if (
                        desktopProcessAction === DesktopProcessActions.spirometry_request_cardioPerfect ||
                        desktopProcessAction === DesktopProcessActions.spirometry_request_careFusion
                    ) {
                        const requestResult = await fetchJson(`/api/requests/${requestId}`, 'GET');
                        await this.props.handleChange(requestResult.json.customControlData);
                    } else if (
                        procedureId &&
                        (desktopProcessAction === DesktopProcessActions.spirometry_procedure_cardioPerfect ||
                            desktopProcessAction === DesktopProcessActions.spirometry_procedure_careFusion)
                    ) {
                        const procedureResult = await fetchJson(`/api/procedures/${procedureId}`, 'GET');
                        await this.props.handleChange(procedureResult.json.customControlData);
                    }

                    await this.setState({ processStatus: HardwareProcessStatus.completed });
                }

                clearInterval(this.desktopProcessUpdateInterval);
                await this.props.setIsProcessing(false);
            }

            timeout += timeoutIntervalSeconds;
        }, timeoutIntervalMilliseconds);
    }

    private async getDesktopProcessStatus() {
        if (this.props.customControlData.desktopProcessId && this.props.requestId) {
            const url = `/api/desktopProcesses/${this.props.customControlData.desktopProcessId}`;
            const desktopProcess = await fetchJson(url, 'GET');

            if (
                desktopProcess.json.status === DesktopProcessStatus.starting ||
                desktopProcess.json.status === DesktopProcessStatus.running ||
                desktopProcess.json.status === DesktopProcessStatus.uploading
            ) {
                await this.props.setIsProcessing(true);
                await this.desktopStatusInterval(
                    desktopProcess.json.action,
                    this.props.requestId,
                    this.props.procedureId
                );
            }
        }
    }

    private async cancelDesktopProcess() {
        if (this.desktopProcessUpdateInterval) {
            clearInterval(this.desktopProcessUpdateInterval);
        }

        await this.setState({ processStatus: undefined, processError: undefined });
        await this.props.handleChange({ importedSpirometerData: null, desktopProcessId: undefined });
        await this.props.setIsProcessing(false);
    }
}

export function initializeCustomControlDataSpirometry(pulmoType: 'procedure' | 'request') {
    return {
        keten_DBC_COPD_astma: pulmoType === 'procedure' ? false : undefined,
        spirogram_inc_reversibiliteitstest: pulmoType === 'procedure' ? false : undefined,
        datum_afname_functieonderzoek: pulmoType === 'procedure' ? DateTime.now().format('YYYY-MM-DD') : undefined,
        gewicht: null,
        lengte: null,
        gender: Gender.unknown,
        klachten: null,
        MRC: null,
        CCQ: null,
        ACQ: null,
        importedSpirometerData: null,
        files: [],
        spirogramData: null,
        conclusie_diagnose: pulmoType === 'procedure' ? ConclusieDiagnoseOption.nog_geen_diagnose : null,
    };
}
